package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*

import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*
import se.mittpass.shared.api.CollectiveAgreementDto
import se.mittpass.shared.api.SaveCollectiveAgreementRequest
import se.mittpass.shared.api.PayrollOBBreakdownDto

private val caScope = MainScope()

@Composable
fun CollectiveAgreementPage(workplaceId: Long) {
    var settings by remember { mutableStateOf<CollectiveAgreementDto?>(null) }
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var feedbackMsg by remember { mutableStateOf<String?>(null) }
    var obBreakdown by remember { mutableStateOf<List<PayrollOBBreakdownDto>>(emptyList()) }
    var showBreakdown by remember { mutableStateOf(false) }
    var calculating by remember { mutableStateOf(false) }
    var fromDate by remember { mutableStateOf(currentMonthStart()) }
    var toDate by remember { mutableStateOf(currentTodayCA()) }

    // Editable fields
    var evPct by remember { mutableStateOf(20) }
    var niPct by remember { mutableStateOf(40) }
    var wePct by remember { mutableStateOf(50) }
    var hoPct by remember { mutableStateOf(100) }
    var otThreshold by remember { mutableStateOf(480) }
    var otPct by remember { mutableStateOf(50) }
    var evStart by remember { mutableStateOf("18:00") }
    var evEnd by remember { mutableStateOf("22:00") }
    var niStart by remember { mutableStateOf("22:00") }
    var niEnd by remember { mutableStateOf("06:00") }
    var paydayDay by remember { mutableStateOf(25) }
    var cutoffDay by remember { mutableStateOf(15) }

    fun loadSettings() {
        loading = true
        caScope.launch {
            collectiveAgreementRepo.getSettings(workplaceId).onSuccess { s ->
                s?.let {
                    evPct = it.obEveningPct; niPct = it.obNightPct; wePct = it.obWeekendPct; hoPct = it.obHolidayPct
                    otThreshold = it.overtimeThresholdDailyMinutes; otPct = it.overtimeRatePct
                    evStart = it.eveningStart; evEnd = it.eveningEnd; niStart = it.nightStart; niEnd = it.nightEnd
                    paydayDay = it.paydayDayOfMonth; cutoffDay = it.cutoffDayOfMonth
                }
                settings = s
            }
            loading = false
        }
    }

    LaunchedEffect(workplaceId) { loadSettings() }

    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1.25rem") } }) {

        // ── Header Card ──
        Div({
            style {
                property("background", "var(--rp-surface)")
                property("border-radius", "16px")
                property("border", "1px solid var(--rp-hl-med)")
                property("padding", "1.25rem 1.5rem")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "space-between")
                property("flex-wrap", "wrap")
                property("gap", "1rem")
            }
        }) {
            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.85rem") } }) {
                Div({
                    style {
                        property("width", "42px")
                        property("height", "42px")
                        property("border-radius", "12px")
                        property("background", "var(--rp-info-bg)")
                        property("color", "var(--rp-pine)")
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                    }
                }) {
                    FaIcon("fa-solid fa-scale-balanced", size = "1.2rem", color = "var(--rp-pine)")
                }
                Div {
                    H2({
                        style {
                            property("margin", "0")
                            property("font-size", "1.25rem")
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                        }
                    }) { Text("Kollektivavtal & OB-regler") }
                    P({
                        style {
                            property("margin", "0.15rem 0 0 0")
                            property("font-size", "0.82rem")
                            property("color", "var(--rp-subtle)")
                        }
                    }) { Text("Konfigurera OB-procent, övertidströsklar och beräkna automatiskt lönetillägg") }
                }
            }

            // Quick status pill
            Div({
                style {
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("gap", "0.5rem")
                    property("background", "var(--rp-ok-bg)")
                    property("border", "1px solid var(--rp-ok-border)")
                    property("padding", "0.4rem 0.85rem")
                    property("border-radius", "9999px")
                    property("font-size", "0.75rem")
                    property("font-weight", "700")
                    property("color", "var(--rp-ok-text)")
                }
            }) {
                FaIcon("fa-solid fa-shield-halved", size = "0.75rem", color = "var(--rp-ok-text)")
                Text("Regelverk aktivt")
            }
        }

        feedbackMsg?.let { msg ->
            val isOk = msg.startsWith("✓")
            Div({
                style {
                    property("background", if (isOk) "var(--rp-ok-bg)" else "var(--rp-err-bg)")
                    property("color", if (isOk) "var(--rp-ok-text)" else "var(--rp-err-text)")
                    property("border", if (isOk) "1px solid var(--rp-ok-border)" else "1px solid var(--rp-err-border)")
                    property("padding", "0.85rem 1.25rem")
                    property("border-radius", "12px")
                    property("font-weight", "700")
                    property("font-size", "0.85rem")
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "0.6rem")
                }
            }) {
                FaIcon(if (isOk) "fa-solid fa-circle-check" else "fa-solid fa-triangle-exclamation", size = "0.9rem", color = if (isOk) "var(--rp-ok-text)" else "var(--rp-err-text)")
                Text(msg)
            }
        }

        if (loading) {
            Div({
                style {
                    property("text-align", "center")
                    property("padding", "4rem 2rem")
                    property("color", "var(--rp-pine)")
                    property("font-weight", "700")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("gap", "0.75rem")
                }
            }) {
                FaIcon("fa-solid fa-spinner fa-spin", size = "1.2rem", color = "var(--rp-pine)")
                Span { Text("Hämtar kollektivavtalsregler…") }
            }
            return@Div
        }

        // ── Main Settings Container ──
        Div({
            style {
                property("background", "var(--rp-surface)")
                property("border-radius", "16px")
                property("border", "1px solid var(--rp-hl-med)")
                property("padding", "1.75rem")
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "1.75rem")
            }
        }) {
            // Group 1: OB-tillägg
            Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1rem") } }) {
                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                    FaIcon("fa-solid fa-percent", size = "0.9rem", color = "var(--rp-pine)")
                    H3({
                        style {
                            property("margin", "0")
                            property("font-size", "1.05rem")
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                        }
                    }) { Text("OB-tillägg i procent") }
                }

                Div({
                    style {
                        property("display", "grid")
                        property("grid-template-columns", "repeat(auto-fit, minmax(220px, 1fr))")
                        property("gap", "1rem")
                    }
                }) {
                    ModernInputField(
                        label = "Kväll OB",
                        subtext = "Procentuellt påslag per timme",
                        value = evPct.toString(),
                        unit = "%",
                        icon = "fa-moon",
                        onChange = { evPct = it.toIntOrNull() ?: 0 }
                    )
                    ModernInputField(
                        label = "Natt OB",
                        subtext = "Procentuellt påslag per timme",
                        value = niPct.toString(),
                        unit = "%",
                        icon = "fa-cloud-moon",
                        onChange = { niPct = it.toIntOrNull() ?: 0 }
                    )
                    ModernInputField(
                        label = "Helg OB",
                        subtext = "Lördag & söndag",
                        value = wePct.toString(),
                        unit = "%",
                        icon = "fa-calendar-week",
                        onChange = { wePct = it.toIntOrNull() ?: 0 }
                    )
                    ModernInputField(
                        label = "Helgdag / Storhelg",
                        subtext = "Röda dagar & storhelger",
                        value = hoPct.toString(),
                        unit = "%",
                        icon = "fa-champagne-glasses",
                        onChange = { hoPct = it.toIntOrNull() ?: 0 }
                    )
                }
            }

            // Divider
            Div({ style { property("height", "1px"); property("background", "var(--rp-overlay)") } }) {}

            // Group 2: Övertid
            Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1rem") } }) {
                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                    FaIcon("fa-solid fa-business-time", size = "0.9rem", color = "var(--rp-pine)")
                    H3({
                        style {
                            property("margin", "0")
                            property("font-size", "1.05rem")
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                        }
                    }) { Text("Övertidsregler") }
                }

                Div({
                    style {
                        property("display", "grid")
                        property("grid-template-columns", "repeat(auto-fit, minmax(260px, 1fr))")
                        property("gap", "1rem")
                    }
                }) {
                    ModernInputField(
                        label = "Daglig tröskel",
                        subtext = "Antal minuter per dag innan övertid infaller",
                        value = otThreshold.toString(),
                        unit = "min (=${otThreshold / 60}h)",
                        icon = "fa-hourglass-half",
                        onChange = { otThreshold = it.toIntOrNull() ?: 0 }
                    )
                    ModernInputField(
                        label = "ÖT-tillägg",
                        subtext = "Påslag vid överskriden tröskel",
                        value = otPct.toString(),
                        unit = "%",
                        icon = "fa-bolt",
                        onChange = { otPct = it.toIntOrNull() ?: 0 }
                    )
                }
            }

            // Divider
            Div({ style { property("height", "1px"); property("background", "var(--rp-overlay)") } }) {}

            // Group 3: Tidsramar
            Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1rem") } }) {
                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                    FaIcon("fa-solid fa-clock", size = "0.9rem", color = "var(--rp-pine)")
                    H3({
                        style {
                            property("margin", "0")
                            property("font-size", "1.05rem")
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                        }
                    }) { Text("Tidsramar för OB-intervall") }
                }

                Div({
                    style {
                        property("display", "grid")
                        property("grid-template-columns", "repeat(auto-fit, minmax(220px, 1fr))")
                        property("gap", "1rem")
                    }
                }) {
                    ModernTimeField(
                        label = "Kväll start",
                        value = evStart,
                        icon = "fa-sun",
                        onChange = { evStart = it }
                    )
                    ModernTimeField(
                        label = "Kväll slut",
                        value = evEnd,
                        icon = "fa-moon",
                        onChange = { evEnd = it }
                    )
                    ModernTimeField(
                        label = "Natt start",
                        value = niStart,
                        icon = "fa-moon",
                        onChange = { niStart = it }
                    )
                    ModernTimeField(
                        label = "Natt slut",
                        value = niEnd,
                        icon = "fa-cloud-sun",
                        onChange = { niEnd = it }
                    )
                }
            }

            // Divider
            Div({ style { property("height", "1px"); property("background", "var(--rp-overlay)") } }) {}

            // Group 4: Utbetalning & Löneperiod
            Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1rem") } }) {
                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                    FaIcon("fa-solid fa-calendar-check", size = "0.9rem", color = "var(--rp-pine)")
                    H3({
                        style {
                            property("margin", "0")
                            property("font-size", "1.05rem")
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                        }
                    }) { Text("Löneperiod & Utbetalningsdag") }
                }

                Div({
                    style {
                        property("display", "grid")
                        property("grid-template-columns", "repeat(auto-fit, minmax(220px, 1fr))")
                        property("gap", "1rem")
                    }
                }) {
                    ModernInputField(
                        label = "Utbetalningsdag",
                        subtext = "T.ex. den 25:e varje månad",
                        value = paydayDay.toString(),
                        unit = ":e",
                        icon = "fa-money-bill-wave",
                        onChange = { paydayDay = it.toIntOrNull()?.coerceIn(1, 31) ?: 25 }
                    )
                    ModernInputField(
                        label = "Brytdatum (startdag)",
                        subtext = if (cutoffDay == 1) "Kalendermånad (1:a till sista)" else "Från $cutoffDay:e till ${cutoffDay - 1}:e",
                        value = cutoffDay.toString(),
                        unit = ":e",
                        icon = "fa-calendar-day",
                        onChange = { cutoffDay = it.toIntOrNull()?.coerceIn(1, 28) ?: 15 }
                    )
                }

                Div({
                    style {
                        property("background", "var(--rp-overlay)"); property("border-radius", "10px"); property("padding", "0.75rem 1rem")
                        property("border", "1px dashed var(--rp-hl-med)"); property("font-size", "0.82rem"); property("color", "var(--rp-subtle)")
                    }
                }) {
                    Span({ style { property("font-weight", "700") } }) { Text("Exempel: ") }
                    if (cutoffDay == 1) {
                        Text("Lön beräknas för hel kalendermånad (1:a till sista) och betalas ut den $paydayDay:e.")
                    } else {
                        Text("Lön beräknas från den $cutoffDay:e till den ${cutoffDay - 1}:e följande månad och betalas ut den $paydayDay:e.")
                    }
                }
            }

            // Save Action Row
            Div({
                style {
                    property("display", "flex")
                    property("justify-content", "flex-end")
                    property("border-top", "1px solid var(--rp-hl-med)")
                    property("padding-top", "1.25rem")
                }
            }) {
                Button({
                    onClick {
                        saving = true
                        caScope.launch {
                            collectiveAgreementRepo.saveSettings(workplaceId, SaveCollectiveAgreementRequest(
                                evPct, niPct, wePct, hoPct, otThreshold, otPct, evStart, evEnd, niStart, niEnd,
                                paydayDayOfMonth = paydayDay,
                                cutoffDayOfMonth = cutoffDay
                            )).onSuccess { feedbackMsg = "✓ Inställningarna har sparats" }
                              .onFailure { feedbackMsg = "Fel: ${it.message}" }
                            saving = false
                        }
                    }
                    if (saving) disabled()
                    style {
                        property("padding", "0.75rem 1.75rem")
                        property("border", "none")
                        property("background", "var(--rp-pine)")
                        property("color", "white")
                        property("border-radius", "12px")
                        property("font-weight", "700")
                        property("font-size", "0.9rem")
                        property("cursor", "pointer")
                        property("display", "flex")
                        property("align-items", "center")
                        property("gap", "0.6rem")
                        property("transition", "all 0.15s ease")
                    }
                }) {
                    FaIcon(if (saving) "fa-solid fa-spinner fa-spin" else "fa-solid fa-check", size = "0.85rem", color = "white")
                    Text(if (saving) "Sparar ändringar…" else "Spara inställningar")
                }
            }
        }

        // ── OB Calculation & Simulation Section ──
        Div({
            style {
                property("background", "var(--rp-surface)")
                property("border-radius", "16px")
                property("border", "1px solid var(--rp-hl-med)")
                property("padding", "1.75rem")
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "1.25rem")
            }
        }) {
            Div({
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "space-between")
                    property("flex-wrap", "wrap")
                    property("gap", "0.75rem")
                }
            }) {
                Div {
                    H3({
                        style {
                            property("margin", "0")
                            property("font-size", "1.1rem")
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "0.5rem")
                        }
                    }) {
                        FaIcon("fa-solid fa-calculator", size = "0.95rem", color = "var(--rp-pine)")
                        Text("Beräkna och granska OB")
                    }
                    P({
                        style {
                            property("margin", "0.2rem 0 0 0")
                            property("font-size", "0.8rem")
                            property("color", "var(--rp-subtle)")
                        }
                    }) { Text("Kör en simulering eller sammanställning av OB-timmar för vald period") }
                }

                // Filter controls
                Div({
                    style {
                        property("display", "flex")
                        property("gap", "0.75rem")
                        property("align-items", "center")
                        property("flex-wrap", "wrap")
                    }
                }) {
                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.4rem") } }) {
                        Span({ style { property("font-size", "0.75rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)") } }) { Text("FRÅN") }
                        Input(InputType.Date) {
                            value(fromDate)
                            onInput { fromDate = it.value }
                            style {
                                property("padding", "0.5rem 0.75rem")
                                property("border", "1px solid var(--rp-hl-med)")
                                property("border-radius", "10px")
                                property("font-size", "0.85rem")
                                property("font-weight", "600")
                                property("color", "var(--rp-text)")
                                property("background", "var(--rp-overlay)")
                            }
                        }
                    }

                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.4rem") } }) {
                        Span({ style { property("font-size", "0.75rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)") } }) { Text("TILL") }
                        Input(InputType.Date) {
                            value(toDate)
                            onInput { toDate = it.value }
                            style {
                                property("padding", "0.5rem 0.75rem")
                                property("border", "1px solid var(--rp-hl-med)")
                                property("border-radius", "10px")
                                property("font-size", "0.85rem")
                                property("font-weight", "600")
                                property("color", "var(--rp-text)")
                                property("background", "var(--rp-overlay)")
                            }
                        }
                    }

                    Button({
                        onClick {
                            calculating = true
                            caScope.launch {
                                collectiveAgreementRepo.getOBBreakdown(workplaceId, fromDate, toDate).onSuccess {
                                    obBreakdown = it; showBreakdown = true
                                }
                                calculating = false
                            }
                        }
                        if (calculating) disabled()
                        style {
                            property("padding", "0.55rem 1.25rem")
                            property("border", "none")
                            property("background", "var(--rp-pine)")
                            property("color", "white")
                            property("border-radius", "10px")
                            property("font-weight", "700")
                            property("font-size", "0.85rem")
                            property("cursor", "pointer")
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "0.5rem")
                        }
                    }) {
                        FaIcon(if (calculating) "fa-solid fa-spinner fa-spin" else "fa-solid fa-play", size = "0.75rem", color = "white")
                        Text(if (calculating) "Beräknar…" else "Visa beräkning")
                    }
                }
            }

            if (showBreakdown) {
                if (obBreakdown.isEmpty()) {
                    Div({
                        style {
                            property("padding", "3rem 1rem")
                            property("color", "var(--rp-muted)")
                            property("text-align", "center")
                            property("font-size", "0.9rem")
                            property("background", "var(--rp-overlay)")
                            property("border-radius", "12px")
                            property("border", "1px dashed var(--rp-hl-med)")
                        }
                    }) {
                        FaIcon("fa-solid fa-calendar-xmark", size = "1.5rem", color = "var(--rp-hl-med)")
                        P({ style { property("margin", "0.5rem 0 0 0"); property("font-weight", "600") } }) {
                            Text("Inga arbetade pass eller löneunderlag i vald period.")
                        }
                    }
                } else {
                    // Summary ribbon of total OB in calculation
                    val totalEve = obBreakdown.sumOf { it.eveningMinutes }
                    val totalNight = obBreakdown.sumOf { it.nightMinutes }
                    val totalWeekend = obBreakdown.sumOf { it.weekendMinutes }
                    val totalOt = obBreakdown.sumOf { it.overtimeMinutes }

                    Div({
                        style {
                            property("display", "grid")
                            property("grid-template-columns", "repeat(auto-fit, minmax(140px, 1fr))")
                            property("gap", "0.75rem")
                        }
                    }) {
                        ObsKpiSummary("Kväll OB", formatHours(totalEve), "var(--rp-warn-bg)", "var(--rp-warn-text)", "fa-moon")
                        ObsKpiSummary("Natt OB", formatHours(totalNight), "var(--rp-info-bg)", "var(--rp-iris)", "fa-cloud-moon")
                        ObsKpiSummary("Helg OB", formatHours(totalWeekend), "var(--rp-info-bg)", "var(--rp-pine)", "fa-calendar-week")
                        ObsKpiSummary("Övertid", formatHours(totalOt), "var(--rp-err-bg)", "var(--rp-err-text)", "fa-business-time")
                    }

                    // Table
                    Div({
                        style {
                            property("border", "1px solid var(--rp-hl-med)")
                            property("border-radius", "12px")
                            property("overflow", "hidden")
                        }
                    }) {
                        // Header
                        Div({
                            style {
                                property("display", "grid")
                                property("grid-template-columns", "1.2fr 90px 80px 80px 80px 80px 80px")
                                property("gap", "0.5rem")
                                property("padding", "0.75rem 1rem")
                                property("background", "var(--rp-overlay)")
                                property("border-bottom", "1px solid var(--rp-hl-med)")
                                property("font-weight", "700")
                                property("font-size", "0.72rem")
                                property("color", "var(--rp-subtle)")
                                property("letter-spacing", "0.4px")
                                property("text-transform", "uppercase")
                            }
                        }) {
                            Span { Text("Medarbetare") }
                            Span { Text("Datum") }
                            Span({ style { property("text-align", "center") } }) { Text("Normal") }
                            Span({ style { property("text-align", "center") } }) { Text("Kväll") }
                            Span({ style { property("text-align", "center") } }) { Text("Natt") }
                            Span({ style { property("text-align", "center") } }) { Text("Helg") }
                            Span({ style { property("text-align", "center") } }) { Text("Övertid") }
                        }

                        obBreakdown.forEach { entry ->
                            Div({
                                style {
                                    property("display", "grid")
                                    property("grid-template-columns", "1.2fr 90px 80px 80px 80px 80px 80px")
                                    property("gap", "0.5rem")
                                    property("padding", "0.75rem 1rem")
                                    property("border-bottom", "1px solid var(--rp-hl-med)")
                                    property("align-items", "center")
                                    property("font-size", "0.85rem")
                                }
                            }) {
                                Span({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) { Text(entry.userName) }
                                Span({ style { property("color", "var(--rp-subtle)"); property("font-size", "0.8rem") } }) { Text(entry.day) }
                                ModernMinBadge(entry.normalMinutes, "var(--rp-ok-bg)", "var(--rp-ok-text)")
                                ModernMinBadge(entry.eveningMinutes, if (entry.eveningMinutes > 0) "var(--rp-warn-bg)" else "var(--rp-overlay)", if (entry.eveningMinutes > 0) "var(--rp-warn-text)" else "var(--rp-muted)")
                                ModernMinBadge(entry.nightMinutes, if (entry.nightMinutes > 0) "var(--rp-info-bg)" else "var(--rp-overlay)", if (entry.nightMinutes > 0) "var(--rp-iris)" else "var(--rp-muted)")
                                ModernMinBadge(entry.weekendMinutes, if (entry.weekendMinutes > 0) "var(--rp-info-bg)" else "var(--rp-overlay)", if (entry.weekendMinutes > 0) "var(--rp-pine)" else "var(--rp-muted)")
                                ModernMinBadge(entry.overtimeMinutes, if (entry.overtimeMinutes > 0) "var(--rp-err-bg)" else "var(--rp-overlay)", if (entry.overtimeMinutes > 0) "var(--rp-err-text)" else "var(--rp-muted)")
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ModernInputField(
    label: String,
    subtext: String,
    value: String,
    unit: String,
    icon: String,
    onChange: (String) -> Unit
) {
    Div({
        style {
            property("background", "var(--rp-overlay)")
            property("border", "1px solid var(--rp-hl-med)")
            property("border-radius", "12px")
            property("padding", "0.85rem 1rem")
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", "0.4rem")
        }
    }) {
        Div({
            style {
                property("display", "flex")
                property("justify-content", "space-between")
                property("align-items", "center")
            }
        }) {
            Span({
                style {
                    property("font-size", "0.72rem")
                    property("font-weight", "800")
                    property("color", "var(--rp-subtle)")
                    property("letter-spacing", "0.4px")
                    property("text-transform", "uppercase")
                }
            }) { Text(label) }

            FaIcon("fa-solid $icon", size = "0.75rem", color = "var(--rp-muted)")
        }

        Div({
            style {
                property("display", "flex")
                property("align-items", "center")
                property("background", "var(--rp-surface)")
                property("border", "1px solid var(--rp-hl-med)")
                property("border-radius", "8px")
                property("padding", "0.2rem 0.6rem")
            }
        }) {
            Input(InputType.Number) {
                value(value)
                onInput { onChange((it.target.asDynamic().value as? String) ?: "") }
                style {
                    property("width", "100%")
                    property("border", "none")
                    property("outline", "none")
                    property("font-size", "1.1rem")
                    property("font-weight", "800")
                    property("color", "var(--rp-text)")
                    property("padding", "0.4rem 0")
                }
            }
            Span({
                style {
                    property("font-size", "0.75rem")
                    property("font-weight", "700")
                    property("color", "var(--rp-subtle)")
                    property("white-space", "nowrap")
                    property("padding-left", "0.4rem")
                }
            }) { Text(unit) }
        }

        Span({
            style {
                property("font-size", "0.72rem")
                property("color", "var(--rp-subtle)")
            }
        }) { Text(subtext) }
    }
}

@Composable
private fun ModernTimeField(
    label: String,
    value: String,
    icon: String,
    onChange: (String) -> Unit
) {
    Div({
        style {
            property("background", "var(--rp-overlay)")
            property("border", "1px solid var(--rp-hl-med)")
            property("border-radius", "12px")
            property("padding", "0.85rem 1rem")
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", "0.4rem")
        }
    }) {
        Div({
            style {
                property("display", "flex")
                property("justify-content", "space-between")
                property("align-items", "center")
            }
        }) {
            Span({
                style {
                    property("font-size", "0.72rem")
                    property("font-weight", "800")
                    property("color", "var(--rp-subtle)")
                    property("letter-spacing", "0.4px")
                    property("text-transform", "uppercase")
                }
            }) { Text(label) }

            FaIcon("fa-solid $icon", size = "0.75rem", color = "var(--rp-muted)")
        }

        Div({
            style {
                property("background", "var(--rp-surface)")
                property("border", "1px solid var(--rp-hl-med)")
                property("border-radius", "8px")
                property("padding", "0.2rem 0.6rem")
            }
        }) {
            Input(InputType.Time) {
                value(value)
                onInput { onChange(it.value) }
                style {
                    property("width", "100%")
                    property("border", "none")
                    property("outline", "none")
                    property("font-size", "1.05rem")
                    property("font-weight", "800")
                    property("color", "var(--rp-text)")
                    property("padding", "0.4rem 0")
                }
            }
        }
    }
}

@Composable
private fun ObsKpiSummary(title: String, value: String, bg: String, color: String, icon: String) {
    Div({
        style {
            property("background", "var(--rp-surface)")
            property("border", "1px solid var(--rp-hl-med)")
            property("border-radius", "12px")
            property("padding", "0.75rem 1rem")
            property("display", "flex")
            property("align-items", "center")
            property("gap", "0.75rem")
        }
    }) {
        Div({
            style {
                property("width", "32px")
                property("height", "32px")
                property("border-radius", "8px")
                property("background", bg)
                property("color", color)
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
        }) {
            FaIcon("fa-solid $icon", size = "0.8rem", color = color)
        }
        Div {
            Div({ style { property("font-size", "0.68rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)") } }) { Text(title) }
            Div({ style { property("font-size", "1.1rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) { Text(value) }
        }
    }
}

@Composable
private fun ModernMinBadge(minutes: Int, bgColor: String, textColor: String) {
    Div({
        style {
            property("display", "flex")
            property("justify-content", "center")
        }
    }) {
        Span({
            style {
                property("background", bgColor)
                property("color", textColor)
                property("padding", "0.2rem 0.5rem")
                property("border-radius", "6px")
                property("font-size", "0.72rem")
                property("font-weight", "700")
                property("text-align", "center")
                property("white-space", "nowrap")
            }
        }) {
            Text(if (minutes == 0) "-" else formatHours(minutes))
        }
    }
}

private fun formatHours(minutes: Int): String {
    val h = minutes / 60
    val m = minutes % 60
    return if (m == 0) "${h}h" else "${h}h ${m}m"
}

private fun currentMonthStart(): String {
    val d = kotlin.js.Date()
    return "${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-01"
}

private fun currentTodayCA(): String {
    val d = kotlin.js.Date()
    return "${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}"
}
